import type React from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import classNames from "classnames";
import { format, isToday } from "date-fns";

import { IDiningOptionEnum } from "@/api/interfaces/configurationLayouts";

const useStyles = createUseStyles(({ spacing, color, font }) => ({
  container: {
    color: color.defaultText,
    display: "flex",
    alignItems: "start",
    gap: spacing.m,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  column: {
    flexDirection: "column",
  },
  label: {
    fontSize: font.size.m,
    fontWeight: font.weight.m,
    flexBasis: "50%",
  },
  timestamp: {
    color: color.orderStatusCardPreOrderTitleTextColor,
    fontSize: font.size.xl,
    fontWeight: font.weight.s,
    lineHeight: font.lineHeight.l,
    textWrap: "nowrap",
  },
  timeBox: {
    display: "flex",
  },
  "timeBox-row": {
    justifyContent: "flex-end",
    flexBasis: "50%",
  },
}));

type OrderCardMobileContentProps = {
  requestedDeliveryDateTime: string;
  diningOptionId: IDiningOptionEnum;
  variant: "column" | "row";
};

export const OrderCardPreOrderBlock: React.FC<OrderCardMobileContentProps> = ({
  requestedDeliveryDateTime,
  diningOptionId,
  variant,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("orderStatusPage");

  const requestedDeliveryObject = new Date(requestedDeliveryDateTime);

  const orderStatusEstimatedTimeTextMap: Partial<
    Record<IDiningOptionEnum, string>
  > = {
    [IDiningOptionEnum.DINE_IN]: t(
      "OrderCardPreOrderBlock.estimatedDeliveryTimeLabel.dineIn",
    ),
    [IDiningOptionEnum.TAKE_OUT]: t(
      "OrderCardPreOrderBlock.estimatedDeliveryTimeLabel.takeOut",
    ),
  };

  const time = format(requestedDeliveryObject, "HH:mm");

  let date = format(requestedDeliveryObject, "dd.MM");

  if (isToday(requestedDeliveryObject)) {
    date = t("OrderCardPreOrderBlock.preOrderDate.today");
  }

  return (
    <div className={classNames(classes.container, classes[variant])}>
      <div className={classes.label}>
        {orderStatusEstimatedTimeTextMap[diningOptionId]}
      </div>
      <div
        className={classNames(
          classes.timeBox,
          variant === "row" && classes["timeBox-row"],
        )}
      >
        <div className={classes.timestamp}>
          {date}, {time}
        </div>
      </div>
    </div>
  );
};
